<script setup lang="ts">
const props = defineProps<{
  open: boolean
  renderOnClose?: boolean
  destroyOnClose?: boolean
}>()

const elem = useTemplateRef('elem')
const data = shallowReactive({
  off: !props.open,
  style: props.open ? undefined : { height: '0px' },
  isRendered: !!props.open || !!props.renderOnClose,
  overflowOn: !!props.open,
  isSlow: undefined as undefined | boolean,
})

const cleanup = useCleanup()
const timer = useTimeout()
const interval = useInterval('UiGrow', true)
const animationFinished = useAnimationFinished(() => elem.value, timer)

function setHeight() {
  const h = myElem(elem.value, 'scrollHeight', 0)
  const prev = data.style?.height
  if (prev && Math.abs(Number.parseFloat(prev) - h) < 10) return
  if (h) data.isSlow = h > 500
  data.style = h > 0 ? { height: `${h}px` } : undefined
}

let syncState: '' | 'open-start' | 'open-done' | 'close-start' | 'close-done' = ''
function sync() {
  timer.clear()

  if (props.open) {
    syncState = 'open-start'
    if (!data.isRendered) data.isRendered = true
    data.off = false
    interval.set(() => syncState === 'open-start' && setHeight(), 30)
    cleanup(
      () => interval.clear(),
      animationFinished({
        ms: 300,
        done: () => {
          if (syncState !== 'open-start') return
          syncState = 'open-done'
          interval.clear()
          data.style = undefined
          data.overflowOn = true
        },
        fail: ({ done }) => done(),
      })
    )
  } else {
    syncState = 'close-start'
    cleanup()
    setHeight()
    data.overflowOn = false
    timer.set(() => {
      data.style = { height: '0px' }
      cleanup(
        animationFinished({
          ms: 300,
          done: () => {
            if (syncState !== 'close-start') return
            syncState = 'close-done'
            interval.clear()
            if (props.destroyOnClose) data.isRendered = false
            data.off = true
            data.style = { height: '0px' }
          },
          fail: ({ done }) => done(),
        })
      )
    }, 5)
  }
}

watch(
  () => props.open,
  () => sync()
)
</script>

<template>
  <div v-if="data.isRendered" ref="elem" :aria-hidden="data.off ? 'true' : 'false'" :class="[$style.grow, data.isSlow && 'slow', data.overflowOn && 'on', data.off && 'off']" :style="data.style">
    <slot />
  </div>
</template>

<style module>
.grow {
  composes: global(_w100p);
  composes: global(_hidden);
  composes: global(_noflex);

  transition: height 180ms var(--tran1);
  will-change: height;
}
.grow:global(.slow) {
  transition: height 380ms var(--tran1);
}
.grow:global(.on) {
  overflow: visible;
}
.grow:global(.off) {
  visibility: hidden;
}
</style>
